/**
 * The ticket for a row of the dashboard's recent transactions. A crypto deposit opens its deposit
 * ticket and a withdrawal (or its return) its withdrawal ticket; every other entry (fees, PnL,
 * adjustments, bank deposits …) opens a transaction ticket in the same style.
 */
import { useEffect, useState } from 'react';
import { D, describeLedger, fmtUsd, fmtUsdSigned, type CryptoPayment, type LedgerEntry, type Withdrawal } from '@platform/shared';
import { cx, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { useVersion } from '../../stores/account';
import { DepositTicket } from './deposit/DepositTicket';
import { Sheet } from './deposit/Sheet';
import { TicketFrame } from './deposit/TicketFrame';
import { AIcon, Chip, CopyButton, PillLink, when, type AcctIconName } from './kit';
import { WithdrawalTicket } from './withdraw/WithdrawalTicket';

export const LEDGER_LABEL: Record<string, [string, AcctIconName]> = {
  deposit: ['Deposit', 'down'],
  withdrawal: ['Withdrawal', 'up'],
  withdrawal_refund: ['Withdrawal returned', 'down'],
  fee: ['Trading fee', 'grid'],
  realized_pnl: ['Realized PnL', 'chart'],
  swap: ['Swap', 'swap'],
  demo_reset: ['Demo funds', 'wallet'],
  adjustment: ['Adjustment', 'wallet'],
  credit: ['Trading credit', 'wallet'],
  cashback: ['Cashback', 'down'],
  referral: ['Referral rewards', 'down'],
  dividend: ['Dividend', 'down'],
  capital_return: ['Capital return', 'up'],
  chargeback: ['Chargeback', 'up'],
  management_fee: ['Management fee', 'grid'],
  performance_fee: ['Performance fee', 'grid'],
  trade_correction: ['Trade correction', 'chart'],
  rollover_adjustment: ['Rollover adjustment', 'swap'],
  trade_transfer: ['Trade transferred', 'chart'],
  balance_transfer: ['Balance transfer', 'down'],
  portfolio_exit_fee: ['Early-exit fee', 'grid'],
  portfolio_lock_bonus: ['Lock bonus', 'down'],
};

/**
 * Label and icon of a ledger entry. A ticket our team opened in the CRM is named by its number
 * ("Withdrawal WD-000012", though booked as an adjustment); its staff note stays internal.
 */
export function ledgerLabel(e: Pick<LedgerEntry, 'type' | 'reason' | 'amount' | 'refType'>): { label: string; icon: AcctIconName } {
  const d = describeLedger(e);
  if (/^(Deposit DEP|Withdrawal WD)-\d{6,}$/.test(d.title)) return { label: d.title, icon: d.kind === 'out' ? 'up' : 'down' };
  const [label, icon] = LEDGER_LABEL[e.type] ?? [e.type.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()), 'wallet' as AcctIconName];
  return { label, icon };
}

/**
 * The ID the client sees for a ledger entry, the same number our team works with in the CRM: the CRM
 * ticket that booked it ("DEP-000021: …"), a deposit request's DR- or a withdrawal's WD- number; the
 * entry's own id otherwise. The reason text itself is never shown, it may be a staff note.
 */
export function transactionId(e: Pick<LedgerEntry, 'id' | 'type' | 'reason' | 'refType'>): string {
  const reason = (e.reason ?? '').trim();
  const crm = e.refType !== 'admin' ? reason.match(/^((?:DEP|WD|ADJ|NOM)-\d{6,}):/) : null;
  if (crm) return crm[1]!;
  const own = e.type === 'deposit' ? reason.match(/\b(DR-\d+)$/) : e.type === 'withdrawal' ? reason.match(/^(WD-\d+)\s+to\s/) : null;
  return own?.[1] ?? e.id;
}

/** describeLedger's detail line without the entry's raw reason text, which may be a staff note ("22"). */
export function ledgerDetail(e: Pick<LedgerEntry, 'type' | 'reason' | 'amount' | 'refType'>): string {
  const { detail } = describeLedger(e);
  return detail && detail === (e.reason ?? '').trim() ? '' : detail;
}

type Loaded = { kind: 'deposit'; p: CryptoPayment } | { kind: 'withdrawal'; w: Withdrawal } | { kind: 'entry' };

export function TransactionTicket({ e, onClose, onChanged }: { e: LedgerEntry; onClose: () => void; onChanged?: () => void }) {
  const crypto = e.refType === 'crypto_payment' && !!e.refId;
  const wd = e.refType === 'withdrawal' && !!e.refId;
  const [got, setGot] = useState<Loaded | null>(crypto || wd ? null : { kind: 'entry' });
  // A withdrawal ticket follows staff decisions as they come in; the deposit ticket follows itself.
  const wV = useVersion('withdrawal');
  useEffect(() => {
    if (!crypto && !wd) return;
    let gone = false;
    const url = crypto ? `/api/deposits/crypto/${e.refId}` : `/api/withdrawals/${e.refId}`;
    api
      .get<CryptoPayment | Withdrawal>(url)
      .then((x) => !gone && setGot(crypto ? { kind: 'deposit', p: x as CryptoPayment } : { kind: 'withdrawal', w: x as Withdrawal }))
      // Not found (or the server is older than this page): fall back to the entry itself.
      .catch(() => !gone && setGot({ kind: 'entry' }));
    return () => {
      gone = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e.id, wd ? wV : 0]);

  if (!got)
    return (
      <Sheet title="Transaction" onClose={onClose}>
        <div className="grid place-items-center py-16 text-muted">
          <Spinner />
        </div>
      </Sheet>
    );
  if (got.kind === 'deposit') return <DepositTicket p={got.p} onClose={onClose} onChanged={onChanged} />;
  if (got.kind === 'withdrawal')
    return (
      <WithdrawalTicket
        w={got.w}
        onClose={onClose}
        onCancelled={() => {
          void api.get<Withdrawal>(`/api/withdrawals/${got.w.id}`).then((w) => setGot({ kind: 'withdrawal', w }), () => {});
          onChanged?.();
        }}
      />
    );
  return <EntryTicket e={e} onClose={onClose} />;
}

/** Any other ledger entry: what it was, how it moved the balance, and its transaction ID. */
function EntryTicket({ e, onClose }: { e: LedgerEntry; onClose: () => void }) {
  const { label, icon } = ledgerLabel(e);
  const id = transactionId(e);
  const amt = D.from(e.amount);
  const credit = D.from(e.creditAmount);
  const rows: [string, string][] = [
    ['Type', label],
    ['Amount', fmtUsdSigned(e.amount)],
  ];
  if (!credit.isZero()) rows.push(['Of which trading credit', fmtUsdSigned(e.creditAmount)], ['Of which cash', fmtUsdSigned(amt.sub(credit).toString())]);
  rows.push(['Balance after', fmtUsd(e.balanceAfter)]);
  if (!credit.isZero() || !D.from(e.creditAfter).isZero()) rows.push(['Credit after', fmtUsd(e.creditAfter)]);
  rows.push(['Date', when(e.createdAt, true)], ['Transaction ID', id]);
  return (
    <Sheet title="Transaction ticket" onClose={onClose}>
      <TicketFrame
        head={
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-4">
              <span className="grid size-11 shrink-0 place-items-center rounded-full bg-track">
                <AIcon name={icon} size={20} />
              </span>
              <div className="min-w-0">
                <div className={cx('num text-[28px] leading-tight font-semibold md:text-[30px]', amt.isNeg() ? 'text-down' : amt.isPos() ? 'text-up' : '')}>{fmtUsdSigned(e.amount)}</div>
                <div className="truncate text-[14px] text-muted">{label}</div>
              </div>
            </div>
            <Chip tone="up">Completed</Chip>
          </div>
        }
      >
        <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-[14px]">
          {rows.map(([t, v]) => (
            <div key={t} className="contents">
              <dt className="text-muted">{t}</dt>
              <dd className="num m-0 text-right break-all">{v}</dd>
            </div>
          ))}
        </dl>
        <div className="flex flex-wrap gap-2">
          <CopyButton value={id} size="sm" kind="secondary">
            Copy transaction ID
          </CopyButton>
          <PillLink to={`/account/support?topic=funding&ref=${encodeURIComponent(id)}`} size="sm" kind="secondary">
            Get help with this transaction
          </PillLink>
        </div>
      </TicketFrame>
    </Sheet>
  );
}
